import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Pill, Trash2, Plus, UserPlus } from "lucide-react";

export default function CoachSupplements() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [recs, setRecs] = useState([]);
  const [recClient, setRecClient] = useState({});
  const [recNote, setRecNote] = useState({});

  const recommend = async (suppId) => {
    try {
      await api.post("/coach/supplement-recs", {
        supplement_id: suppId, client_id: recClient[suppId], note: recNote[suppId] || "",
      });
      toast.success(t("supp.recSaved"));
      setRecClient({ ...recClient, [suppId]: "" });
      setRecNote({ ...recNote, [suppId]: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeRec = async (recId) => {
    try {
      await api.delete(`/coach/supplement-recs/${recId}`);
      toast.success(t("supp.deleted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard"), end: true },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  const load = () => {
    api.get("/supplements").then((r) => setItems(r.data)).catch(() => {});
    api.get("/coach/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/coach/supplement-recs").then((r) => setRecs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/supplements", {
        name, description,
        price: price === "" ? null : parseFloat(price.replace(",", ".")),
        url,
      });
      toast.success(t("supp.saved"));
      setName(""); setDescription(""); setPrice(""); setUrl("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/supplements/${id}`);
      toast.success(t("supp.deleted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Layout nav={nav}>
      <div className="max-w-3xl space-y-8" data-testid="coach-supplements-page">
        <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
          <Pill className="w-7 h-7 text-primary" /> {t("supp.title")}
        </h1>

        <form onSubmit={add} className="kc-card p-5 space-y-4" data-testid="supplement-form">
          <h2 className="font-bold">{t("supp.add")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("supp.name")}</label>
              <input data-testid="supplement-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="kc-input mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("supp.price")}</label>
              <input data-testid="supplement-price-input" type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="kc-input mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("supp.desc")}</label>
            <textarea data-testid="supplement-desc-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="kc-input mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("supp.url")}</label>
            <input data-testid="supplement-url-input" type="url" placeholder="https://..." value={url} onChange={(e) => setUrl(e.target.value)} className="kc-input mt-1" />
          </div>
          <button data-testid="supplement-submit-button" type="submit" disabled={saving || !name.trim()} className="kc-btn-primary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> {saving ? t("common.loading") : t("supp.add")}
          </button>
        </form>

        <div className="space-y-3" data-testid="supplements-list">
          {items.length === 0 && <p className="text-muted-foreground text-sm">{t("supp.empty")}</p>}
          {items.map((s) => (
            <div key={s._id} className="kc-card p-4" data-testid="coach-supplement-card">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-bold">
                    {s.name}
                    {s.price != null && <span className="font-mono text-primary ml-2">€{Number(s.price).toFixed(2)}</span>}
                  </p>
                  {s.description && <p className="text-sm text-muted-foreground mt-0.5">{s.description}</p>}
                  {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline break-all">{s.url}</a>}
                </div>
                <button data-testid={`supplement-delete-${s._id}`} onClick={() => remove(s._id)} className="kc-btn-ghost !px-2.5 shrink-0">
                  <Trash2 className="w-4 h-4 text-destructive" />
                </button>
              </div>
              <div className="mt-3 pt-3 border-t border-border space-y-2">
                {recs.filter((r) => r.supplement_id === s._id).map((r) => (
                  <div key={r._id} className="flex items-center justify-between gap-2 text-xs" data-testid={`rec-chip-${r._id}`}>
                    <span className="font-semibold text-accent">{r.client_name}{r.note ? <span className="text-muted-foreground font-normal"> — {r.note}</span> : ""}</span>
                    <button data-testid={`rec-delete-${r._id}`} onClick={() => removeRec(r._id)} className="kc-btn-ghost !p-1 text-destructive shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <div className="flex flex-col sm:flex-row gap-2">
                  <select data-testid={`rec-client-${s._id}`} value={recClient[s._id] || ""}
                    onChange={(e) => setRecClient({ ...recClient, [s._id]: e.target.value })} className="kc-input !py-2 text-sm flex-1">
                    <option value="">{t("supp.recommendTo")}</option>
                    {clients.map((c) => <option key={c._id} value={c._id}>{c.name} ({c.email})</option>)}
                  </select>
                  <input data-testid={`rec-note-${s._id}`} placeholder={t("supp.noteOptional")} value={recNote[s._id] || ""}
                    onChange={(e) => setRecNote({ ...recNote, [s._id]: e.target.value })} className="kc-input !py-2 text-sm flex-1" />
                  <button data-testid={`rec-send-${s._id}`} onClick={() => recommend(s._id)} disabled={!recClient[s._id]}
                    className="kc-btn-secondary !px-3 !py-2 text-xs inline-flex items-center justify-center gap-1.5 shrink-0">
                    <UserPlus className="w-3.5 h-3.5" /> {t("supp.recommend")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
